<template>
  <div>
    <div class="card">
      <div class="card-header border-0 pb-0">
        <ul class="nav nav-tabs nav-tabs-line card-header-tabs nav-fill" role="tablist">
          <li class="nav-item" role="presentation">
            <a class="nav-link" :class="{ active: tab === 'infos' }" href="#" @click.prevent="tab = 'infos'">
              Infos
            </a>
          </li>
          <li class="nav-item" role="presentation">
            <a class="nav-link" :class="{ active: tab === 'medias' }" href="#" @click.prevent="tab = 'medias'">
              Médias
            </a>
          </li>
          <li class="nav-item" role="presentation">
            <a class="nav-link" :class="{ active: tab === 'signatures' }" href="#" @click.prevent="tab = 'signatures'">
              Signatures
            </a>
          </li>
          <li class="nav-item" role="presentation">
            <a class="nav-link" :class="{ active: tab === 'connexions' }" href="#" @click.prevent="openConnexionsTab">
              Connexions
            </a>
          </li>
        </ul>
      </div>

      <div class="card-body" v-if="loaded">

        <!-- TAB 1 : Infos -->
        <div v-if="tab === 'infos'">
          <div class="mb-3">
            <label class="form-label fw-semibold">Notes</label>
            <textarea class="form-control" v-model="form.notes" rows="4"></textarea>
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold">Phrases interdites</label>
            <textarea class="form-control" v-model="form.forbiddenPhrases" rows="3"></textarea>
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold d-block">Notifications</label>
            <div class="form-check mb-2">
              <input class="form-check-input" type="checkbox" v-model="form.batchNotifications" id="setting-batch">
              <label class="form-check-label" for="setting-batch">Groupées (batch)</label>
            </div>
            <div class="d-flex flex-wrap gap-3">
              <div v-for="day in days" :key="day.key" class="form-check">
                <input class="form-check-input" type="checkbox" v-model="form[day.key]" :id="'setting-' + day.key">
                <label class="form-check-label" :for="'setting-' + day.key">{{ day.label }}</label>
              </div>
            </div>
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold">Site officiel</label>
            <input class="form-control" type="url" v-model="form.website" placeholder="https://...">
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold">Page Facebook</label>
            <input class="form-control" type="url" v-model="form.pageReseauPublic" placeholder="https://facebook.com/...">
          </div>

          <div class="mb-3">
            <label class="form-label fw-semibold">Page Instagram</label>
            <input class="form-control" type="url" v-model="form.pageReseauPublic2" placeholder="https://instagram.com/...">
          </div>

          <button class="btn btn-primary" @click="saveInfos" :disabled="saving">
            {{ saving ? 'Enregistrement...' : 'Enregistrer' }}
          </button>
          <span v-if="saveSuccess" class="ms-3 text-success small">Enregistré !</span>
        </div>

        <!-- TAB 2 : Médias -->
        <div v-if="tab === 'medias'">
          <div class="row g-4">
            <div class="col-12 col-md-6">
              <label class="form-label fw-semibold">Logo</label>
              <div class="mb-2">
                <img v-if="logoPreview" :src="logoPreview"
                     class="img-thumbnail d-block mb-2"
                     style="max-height:120px; max-width:200px; object-fit:contain;">
                <div v-else class="border rounded d-flex align-items-center justify-content-center mb-2 text-muted"
                     style="height:120px;width:120px;">
                  <i class="bi bi-image" style="font-size:2rem;"></i>
                </div>
              </div>
              <input type="file" accept="image/*" class="form-control form-control-sm" @change="uploadLogo">
              <div v-if="uploadingLogo" class="text-muted small mt-1">Envoi en cours...</div>
            </div>

            <div class="col-12 col-md-6">
              <label class="form-label fw-semibold">Photo principale</label>
              <div class="mb-2">
                <img v-if="mainPicturePreview" :src="mainPicturePreview"
                     class="img-thumbnail d-block mb-2"
                     style="max-height:120px; max-width:200px; object-fit:contain;">
                <div v-else class="border rounded d-flex align-items-center justify-content-center mb-2 text-muted"
                     style="height:120px;width:120px;">
                  <i class="bi bi-image" style="font-size:2rem;"></i>
                </div>
              </div>
              <input type="file" accept="image/*" class="form-control form-control-sm" @change="uploadMainPicture">
              <div v-if="uploadingMain" class="text-muted small mt-1">Envoi en cours...</div>
            </div>
          </div>
        </div>

        <!-- TAB 3 : Signatures -->
        <div v-if="tab === 'signatures'">
          <div class="d-flex justify-content-between align-items-center mb-3">
            <span class="fw-semibold">Signatures ({{ signatures.length }})</span>
            <button class="btn btn-sm btn-outline-primary" @click="openSigForm(null)">+ Ajouter</button>
          </div>

          <div v-for="sig in signatures" :key="sig.uid" class="card mb-2">
            <div class="card-body py-2 d-flex justify-content-between align-items-start">
              <div style="min-width:0;">
                <span class="badge bg-secondary me-2">{{ sig.language ? sig.language.code : '?' }}</span>
                <strong>{{ sig.title }}</strong>
                <div class="text-muted small mt-1"
                     style="white-space:pre-wrap; overflow:hidden; max-height:40px;">{{ truncate(sig.content, 100) }}</div>
              </div>
              <div class="d-flex gap-1 ms-3 flex-shrink-0">
                <button class="btn btn-sm btn-outline-secondary" @click="openSigForm(sig)">
                  <i class="bi bi-pencil"></i>
                </button>
                <button class="btn btn-sm btn-outline-danger" @click="deleteSig(sig)">
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>

          <div v-if="signatures.length === 0 && !sigForm" class="text-muted text-center py-4">
            Aucune signature
          </div>

          <!-- Formulaire signature -->
          <div v-if="sigForm" class="card mt-3 border-primary">
            <div class="card-body">
              <h6 class="mb-3">{{ sigForm.uid ? 'Modifier la signature' : 'Nouvelle signature' }}</h6>
              <div class="mb-2">
                <label class="form-label">Langue</label>
                <select class="form-select" v-model="sigForm.language">
                  <option value="FR">Français (FR)</option>
                  <option value="EN">Anglais (EN)</option>
                  <option value="DE">Allemand (DE)</option>
                  <option value="ES">Espagnol (ES)</option>
                  <option value="IT">Italien (IT)</option>
                  <option value="NL">Néerlandais (NL)</option>
                  <option value="PT">Portugais (PT)</option>
                </select>
              </div>
              <div class="mb-2">
                <label class="form-label">Titre</label>
                <input class="form-control" v-model="sigForm.title" placeholder="Titre de la signature">
              </div>
              <div class="mb-2">
                <label class="form-label">Contenu</label>
                <textarea class="form-control" v-model="sigForm.content" rows="6"></textarea>
              </div>
              <div class="d-flex gap-2">
                <button class="btn btn-primary btn-sm" @click="saveSig" :disabled="savingSig">
                  {{ savingSig ? 'Enregistrement...' : 'Enregistrer' }}
                </button>
                <button class="btn btn-outline-secondary btn-sm" @click="sigForm = null">Annuler</button>
              </div>
            </div>
          </div>
        </div>

        <!-- TAB 4 : Connexions -->
        <div v-if="tab === 'connexions'">
          <div v-if="loadingConn" class="text-center py-3">
            <div class="spinner-border spinner-border-sm text-primary" role="status"></div>
          </div>

          <div v-for="conn in connections" :key="conn.uid" class="card mb-3">
            <div class="card-body">
              <h6 class="mb-3">{{ conn.platform ? conn.platform.name : '?' }}</h6>
              <div class="mb-2">
                <label class="form-label">Login</label>
                <input class="form-control" v-model="connEdits[conn.uid].login" placeholder="Identifiant">
              </div>
              <div class="mb-3">
                <label class="form-label">Mot de passe</label>
                <div class="input-group">
                  <input
                    class="form-control"
                    :type="connEdits[conn.uid].showPass ? 'text' : 'password'"
                    v-model="connEdits[conn.uid].password"
                    placeholder="Mot de passe"
                    autocomplete="new-password"
                  >
                  <button
                    class="btn btn-outline-secondary"
                    type="button"
                    @click="connEdits[conn.uid].showPass = !connEdits[conn.uid].showPass"
                  >
                    <i :class="connEdits[conn.uid].showPass ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                  </button>
                </div>
              </div>
              <button class="btn btn-primary btn-sm" @click="saveConn(conn)" :disabled="connEdits[conn.uid].saving">
                {{ connEdits[conn.uid].saving ? 'Enregistrement...' : 'Enregistrer' }}
              </button>
            </div>
          </div>

          <div v-if="connections.length === 0 && !loadingConn" class="text-muted text-center py-4">
            Aucune connexion
          </div>
        </div>

      </div>

      <div v-else class="card-body d-flex justify-content-center py-5">
        <div class="spinner-border text-primary" role="status"></div>
      </div>
    </div>
  </div>
</template>

<script>
import settings from '@/settings.coffee'
import ESTABLISHMENT_SETTINGS from '@/gql/queries/establishmentSettings.graphql'
import UPDATE_ESTABLISHMENT from '@/gql/mutations/updateEstablishment.graphql'
import ESTABLISHMENT_UPDATE_LOGO from '@/gql/mutations/establishmentUpdateLogo.graphql'
import ESTABLISHMENT_UPDATE_MAIN_PICTURE from '@/gql/mutations/establishmentUpdateMainPicture.graphql'
import UPSERT_ESTABLISHMENT_SIGNATURE from '@/gql/mutations/establishmentSignature.graphql'
import DELETE_ESTABLISHMENT_SIGNATURE from '@/gql/mutations/deleteEstablishmentSignature.graphql'
import CONNECTION_SET from '@/gql/queries/connectionSet.graphql'
import UPSERT_CONNECTION from '@/gql/mutations/upsertConnection.graphql'
import { gqlQuery, gqlMutation } from '@/gqlClient'

export default {
  name: 'EstablishmentSettings',
  props: {
    establishment: { type: Object, required: true }
  },
  data() {
    return {
      tab: 'infos',
      loaded: false,
      saving: false,
      saveSuccess: false,
      savingSig: false,
      connections: [],
      connEdits: {},
      loadingConn: false,
      uploadingLogo: false,
      uploadingMain: false,
      logoPreview: null,
      mainPicturePreview: null,
      signatures: [],
      sigForm: null,
      form: {
        notes: '',
        forbiddenPhrases: '',
        batchNotifications: false,
        notifyMon: false,
        notifyTue: false,
        notifyWed: false,
        notifyThu: false,
        notifyFri: false,
        notifySat: false,
        notifySun: false,
        website: '',
        pageReseauPublic: '',
        pageReseauPublic2: ''
      },
      days: [
        { key: 'notifyMon', label: 'Lun' },
        { key: 'notifyTue', label: 'Mar' },
        { key: 'notifyWed', label: 'Mer' },
        { key: 'notifyThu', label: 'Jeu' },
        { key: 'notifyFri', label: 'Ven' },
        { key: 'notifySat', label: 'Sam' },
        { key: 'notifySun', label: 'Dim' }
      ]
    }
  },
  watch: {
    establishment: {
      immediate: true,
      handler(val) {
        if (val && val.uid) this.fetchSettings()
      }
    }
  },
  methods: {
    truncate(str, n) {
      if (!str) return ''
      return str.length > n ? str.slice(0, n) + '...' : str
    },
    async fetchSettings() {
      this.loaded = false
      const res = await gqlQuery(ESTABLISHMENT_SETTINGS, { id: this.establishment.uid })
      const data = res.body.data.establishment
      if (!data) { this.loaded = true; return }

      this.form.notes = data.notes || ''
      this.form.forbiddenPhrases = data.forbidden_phrases || ''
      this.form.batchNotifications = !!data.batch_notifications
      this.form.notifyMon = !!data.notify_mon
      this.form.notifyTue = !!data.notify_tue
      this.form.notifyWed = !!data.notify_wed
      this.form.notifyThu = !!data.notify_thu
      this.form.notifyFri = !!data.notify_fri
      this.form.notifySat = !!data.notify_sat
      this.form.notifySun = !!data.notify_sun
      this.form.website = data.website || ''
      this.form.pageReseauPublic = data.page_reseau_public || ''
      this.form.pageReseauPublic2 = data.page_reseau_public_2 || ''

      this.logoPreview = data.logo ? settings.backendMediaUrl + data.logo : null
      this.mainPicturePreview = data.main_picture ? settings.backendMediaUrl + data.main_picture : null
      this.signatures = data.signature_set || []
      this.loaded = true
    },
    async saveInfos() {
      this.saving = true
      this.saveSuccess = false
      await gqlMutation(UPDATE_ESTABLISHMENT, {
        uid: this.establishment.uid,
        name: this.establishment.name,
        searchVector: this.establishment.name,
        notes: this.form.notes || null,
        forbiddenPhrases: this.form.forbiddenPhrases || null,
        batchNotifications: this.form.batchNotifications,
        notifyMon: this.form.notifyMon,
        notifyTue: this.form.notifyTue,
        notifyWed: this.form.notifyWed,
        notifyThu: this.form.notifyThu,
        notifyFri: this.form.notifyFri,
        notifySat: this.form.notifySat,
        notifySun: this.form.notifySun,
        website: this.form.website || null,
        pageReseauPublic: this.form.pageReseauPublic || null,
        pageReseauPublic2: this.form.pageReseauPublic2 || null
      })
      this.saving = false
      this.saveSuccess = true
      setTimeout(() => { this.saveSuccess = false }, 3000)
    },
    uploadFile(file, onBase64) {
      if (!file) return
      if (file.size > 5000000) {
        this.$store.dispatch('notify', { level: 'danger', text: 'Image trop lourde (max 5 Mo)' })
        return
      }
      const reader = new FileReader()
      reader.onload = (e) => onBase64(e.target.result, file.name)
      reader.readAsDataURL(file)
    },
    uploadLogo(event) {
      this.uploadFile(event.target.files[0], async (imgdata, imgFileName) => {
        this.uploadingLogo = true
        await gqlMutation(ESTABLISHMENT_UPDATE_LOGO, { uid: this.establishment.uid, imgdata, imgFileName })
        this.logoPreview = imgdata
        this.uploadingLogo = false
      })
    },
    uploadMainPicture(event) {
      this.uploadFile(event.target.files[0], async (imgdata, imgFileName) => {
        this.uploadingMain = true
        await gqlMutation(ESTABLISHMENT_UPDATE_MAIN_PICTURE, { uid: this.establishment.uid, imgdata, imgFileName })
        this.mainPicturePreview = imgdata
        this.uploadingMain = false
      })
    },
    openSigForm(sig) {
      if (sig) {
        this.sigForm = {
          uid: sig.uid,
          language: sig.language ? sig.language.code : 'FR',
          title: sig.title || '',
          content: sig.content || ''
        }
      } else {
        this.sigForm = { uid: null, language: 'FR', title: '', content: '' }
      }
    },
    async saveSig() {
      if (!this.sigForm.title || !this.sigForm.content) return
      this.savingSig = true
      const res = await gqlMutation(UPSERT_ESTABLISHMENT_SIGNATURE, {
        uid: this.sigForm.uid || undefined,
        establishment: this.establishment.uid,
        language: this.sigForm.language,
        title: this.sigForm.title,
        content: this.sigForm.content
      })
      const saved = res.body.data.establishmentSignatureUpsert
      if (saved && !saved.errors.length) {
        if (this.sigForm.uid) {
          const idx = this.signatures.findIndex(s => s.uid === this.sigForm.uid)
          if (idx !== -1) {
            this.$set(this.signatures, idx, {
              uid: saved.uid,
              title: saved.title,
              content: saved.content,
              language: { code: saved.language }
            })
          }
        } else {
          this.signatures.push({
            uid: saved.uid,
            title: saved.title,
            content: saved.content,
            language: { code: saved.language }
          })
        }
        this.sigForm = null
      }
      this.savingSig = false
    },
    async deleteSig(sig) {
      if (!confirm(`Supprimer la signature "${sig.title}" ?`)) return
      await gqlMutation(DELETE_ESTABLISHMENT_SIGNATURE, { uid: sig.uid })
      this.signatures = this.signatures.filter(s => s.uid !== sig.uid)
    },
    async openConnexionsTab() {
      this.tab = 'connexions'
      await this.fetchConnections()
    },
    async fetchConnections() {
      this.loadingConn = true
      const res = await gqlQuery(CONNECTION_SET, { establishment_uid: this.establishment.uid })
      this.connections = res.body.data.connection_set || []
      const edits = {}
      this.connections.forEach(c => {
        edits[c.uid] = { login: c.login || '', password: c.password || '', showPass: false, saving: false }
      })
      this.connEdits = edits
      this.loadingConn = false
    },
    async saveConn(conn) {
      const edit = this.connEdits[conn.uid]
      this.$set(edit, 'saving', true)
      await gqlMutation(UPSERT_CONNECTION, {
        uid: conn.uid,
        establishment: this.establishment.uid,
        platform: conn.platform.uid,
        login: edit.login || null,
        password: edit.password || null
      })
      this.$set(edit, 'saving', false)
      this.$store.dispatch('notify', { level: 'success', text: 'Enregistré !', seconds: 2 })
    }
  }
}
</script>
